import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, MapPin, Plus } from "lucide-react";
import { REALISATIONS } from "../data/site";
import { FadeUp, SectionHead, EASE } from "./motion";
import { useLang } from "../context/LangContext";
import { BeforeAfter } from "./BeforeAfter";

export const Realisations = () => {
  const [cat, setCat] = useState("all");
  const [active, setActive] = useState(null);
  const { S, lang } = useLang();
  const R = S.realisations;

  const filtered = useMemo(
    () => (cat === "all" ? REALISATIONS : REALISATIONS.filter((r) => r.cat === cat)),
    [cat]
  );

  useEffect(() => {
    if (active !== null) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [active]);

  const current = active !== null ? filtered[active] : null;
  const catLabel = (id) => R.categories.find((c) => c.id === id)?.label || id;

  return (
    <section id="realisations" data-testid="realisations-section" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow={R.eyebrow}
          title={R.title}
          subtitle={R.subtitle}
        />

        <FadeUp delay={0.2} className="mt-10">
          <div className="flex flex-wrap gap-2.5" role="tablist" aria-label="Filtres">
            {R.categories.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={cat === c.id}
                data-testid={`gallery-filter-${c.id}`}
                onClick={() => {
                  setCat(c.id);
                  setActive(null);
                }}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  cat === c.id
                    ? "bg-[#0A182E] text-white shadow-lg shadow-slate-900/20"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-sky-400 hover:text-sky-700"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </FadeUp>

        <motion.div layout className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((r, i) => (
              <motion.figure
                layout
                key={r.src}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="group relative aspect-[4/3] overflow-hidden rounded-2xl cursor-pointer bg-slate-200"
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${r.cat}-${i}`}
              >
                <img
                  src={r.src}
                  alt={`${r.title[lang]} — ${r.loc}, GPN Sàrl`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060F1F]/85 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <span className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 backdrop-blur-md text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <Plus className="w-5 h-5" />
                </span>
                <figcaption className="absolute bottom-0 inset-x-0 p-5">
                  <span className="font-tech text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-400">
                    {catLabel(r.cat)}
                  </span>
                  <p className="mt-1 font-display text-base font-semibold text-white">{r.title[lang]}</p>
                  <p className="text-xs text-slate-300 inline-flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> {r.loc}
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>

        {S.realisations.ba && (
          <FadeUp delay={0.15} className="mt-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <p className="font-tech text-xs font-semibold uppercase tracking-[0.28em] text-sky-600">
                  {S.realisations.ba.eyebrow}
                </p>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                  {S.realisations.ba.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{S.realisations.ba.desc}</p>
              </div>
              <BeforeAfter
                beforeSrc="/realisations/gpn-04.jpg"
                afterSrc="/realisations/gpn-05.jpg"
                beforeLabel={S.realisations.ba.before}
                afterLabel={S.realisations.ba.after}
                altBefore="Escalier avant habillage — GPN Sàrl"
                altAfter="Escalier carrelé grand format — GPN Sàrl"
              />
            </div>
          </FadeUp>
        )}
      </div>

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#060F1F]/95 backdrop-blur-md p-4 sm:p-10"
            onClick={() => setActive(null)}
            data-lenis-prevent
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-sky-600 transition-colors"
              aria-label={R.closeAria}
            >
              <X className="w-5 h-5" />
            </button>
            {filtered.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActive((active - 1 + filtered.length) % filtered.length);
                  }}
                  className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-sky-600 transition-colors"
                  aria-label={R.prevAria}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  data-testid="lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActive((active + 1) % filtered.length);
                  }}
                  className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-sky-600 transition-colors"
                  aria-label={R.nextAria}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <motion.div
              key={current.src}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={current.src}
                alt={`${current.title[lang]} — ${current.loc}`}
                className="w-full max-h-[75vh] object-contain rounded-xl shadow-2xl"
              />
              <div className="mt-5 flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <span className="font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-400">
                    {catLabel(current.cat)}
                  </span>
                  <p className="font-display text-xl font-semibold text-white mt-1">{current.title[lang]}</p>
                </div>
                <p className="text-sm text-slate-400 inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-sky-400" /> {current.loc}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
